import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  LayoutDashboard, TrendingUp, TrendingDown, DollarSign, Users,
  AlertTriangle, Calculator, Percent, ArrowRight, Info,
  BarChart3, PieChart, FileText, Target
} from 'lucide-react';

export default function ExecutivoDashboard() {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  const [memoriaAberta, setMemoriaAberta] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    loadDados();
  }, [filtros.mes, filtros.ano]);

  const loadDados = async () => {
    setLoading(true);
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const [response, canonicalRes] = await Promise.all([
        api.get('/executivo/resumo', { params: filtros }),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/executive', { params: { year: filtros.ano } }).catch(() => ({ data: null })),
      ]);
      const merged = response.data || {};
      if (canonicalRes.data) {
        console.info('[executivo] motor canônico', canonicalRes.data);
        merged.cards = merged.cards || {};
        // Sobrescreve com verdade canônica (db.titles + finance_events)
        merged.cards.receita_recebida = {
          ...(merged.cards.receita_recebida || {}),
          valor: canonicalRes.data.recebido,
          memoria: { ...(merged.cards.receita_recebida?.memoria || {}), fonte: 'canônico · finance_events' },
        };
        merged.cards.receita_faturada = {
          ...(merged.cards.receita_faturada || {}),
          valor: canonicalRes.data.receita_prevista,
          memoria: { ...(merged.cards.receita_faturada?.memoria || {}), fonte: 'canônico · titles' },
        };
        merged.cards.inadimplencia = {
          ...(merged.cards.inadimplencia || {}),
          valor: canonicalRes.data.vencido,
          percentual: canonicalRes.data.inadimplencia_pct,
          memoria: { ...(merged.cards.inadimplencia?.memoria || {}), fonte: 'canônico · effective_status(vencido)' },
        };
        merged._engine = 'canonical';
      }
      setDados(merged);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const CardComMemoria = ({ titulo, valor, subtitulo, icone: Icon, cor, memoria }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{titulo}</CardTitle>
        <div className="flex items-center gap-1">
          {memoria && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Info className="w-3 h-3 text-muted-foreground" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Memória de Cálculo: {titulo}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium">Fórmula:</p>
                    <code className="text-sm">{memoria.formula}</code>
                  </div>
                  {memoria.componentes && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium mb-2">Componentes:</p>
                      <ul className="text-sm space-y-1">
                        {Object.entries(memoria.componentes).map(([k, v]) => (
                          <li key={k} className="flex justify-between">
                            <span className="text-muted-foreground">{k}:</span>
                            <span className="font-medium">{typeof v === 'number' ? formatCurrency(v) : v}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Origem: {memoria.origem_dados} | Calculado em: {new Date(memoria.calculado_em).toLocaleString('pt-BR')}
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <Icon className={`w-5 h-5 ${cor}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${cor}`}>{valor}</div>
        {subtitulo && <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="executivo-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LayoutDashboard className="w-7 h-7 text-purple-600" />
            Dashboard Financeiro Executivo
          </h1>
          <p className="text-muted-foreground">
            {dados?.periodo?.nome_mes} de {dados?.periodo?.ano}
          </p>
        </div>
        <div className="flex gap-2">
          <Select 
            value={String(filtros.mes)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, mes: parseInt(v) }))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={String(filtros.ano)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, ano: parseInt(v) }))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(ano => (
                <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Alertas */}
      {dados?.alertas?.length > 0 && (
        <div className="space-y-2">
          {dados.alertas.map((alerta, i) => (
            <Card key={i} className={`border-l-4 ${
              alerta.severidade === 'alta' ? 'border-l-red-500 bg-red-50' : 'border-l-amber-500 bg-amber-50'
            }`}>
              <CardContent className="flex items-center gap-3 py-3">
                <AlertTriangle className={`w-5 h-5 ${
                  alerta.severidade === 'alta' ? 'text-red-600' : 'text-amber-600'
                }`} />
                <p className={`text-sm ${
                  alerta.severidade === 'alta' ? 'text-red-700' : 'text-amber-700'
                }`}>
                  {alerta.mensagem}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cards Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CardComMemoria
          titulo="Receita Faturada"
          valor={formatCurrency(dados?.cards?.receita_faturada?.valor)}
          icone={DollarSign}
          cor="text-green-600"
          memoria={dados?.cards?.receita_faturada?.memoria}
        />
        <CardComMemoria
          titulo="Receita Recebida"
          valor={formatCurrency(dados?.cards?.receita_recebida?.valor)}
          icone={DollarSign}
          cor="text-blue-600"
          memoria={dados?.cards?.receita_recebida?.memoria}
        />
        <CardComMemoria
          titulo="Inadimplência"
          valor={formatCurrency(dados?.cards?.inadimplencia?.valor)}
          subtitulo={`${dados?.cards?.inadimplencia?.percentual || 0}% da receita`}
          icone={TrendingDown}
          cor="text-orange-600"
          memoria={dados?.cards?.inadimplencia?.memoria}
        />
        <CardComMemoria
          titulo="Despesas"
          valor={formatCurrency(dados?.cards?.despesas?.valor)}
          icone={TrendingDown}
          cor="text-red-600"
          memoria={dados?.cards?.despesas?.memoria}
        />
      </div>

      {/* Cards EBITDA e Lucro */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CardComMemoria
          titulo="EBITDA"
          valor={formatCurrency(dados?.cards?.ebitda?.valor)}
          icone={TrendingUp}
          cor={dados?.cards?.ebitda?.valor >= 0 ? "text-green-600" : "text-red-600"}
          memoria={dados?.cards?.ebitda?.memoria}
        />
        <CardComMemoria
          titulo="Tributos Estimados"
          valor={formatCurrency(dados?.cards?.tributos?.valor)}
          subtitulo={dados?.cards?.tributos?.apurado ? "Apurado" : "Não apurado"}
          icone={Calculator}
          cor="text-purple-600"
        />
        <CardComMemoria
          titulo="Lucro Líquido"
          valor={formatCurrency(dados?.cards?.lucro_liquido?.valor)}
          icone={Target}
          cor={dados?.cards?.lucro_liquido?.valor >= 0 ? "text-green-600" : "text-red-600"}
          memoria={dados?.cards?.lucro_liquido?.memoria}
        />
        <CardComMemoria
          titulo="Alunos Ativos"
          valor={dados?.cards?.total_alunos?.valor || 0}
          icone={Users}
          cor="text-blue-600"
        />
      </div>

      {/* Cards de Margens e Tickets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CardComMemoria
          titulo="Margem EBITDA"
          valor={`${dados?.cards?.margem_ebitda?.valor || 0}%`}
          icone={Percent}
          cor={dados?.cards?.margem_ebitda?.valor >= 25 ? "text-green-600" : "text-amber-600"}
          memoria={dados?.cards?.margem_ebitda?.memoria}
        />
        <CardComMemoria
          titulo="Margem Líquida"
          valor={`${dados?.cards?.margem_liquida?.valor || 0}%`}
          icone={Percent}
          cor={dados?.cards?.margem_liquida?.valor >= 15 ? "text-green-600" : "text-amber-600"}
          memoria={dados?.cards?.margem_liquida?.memoria}
        />
        <CardComMemoria
          titulo="Ticket Médio"
          valor={formatCurrency(dados?.cards?.ticket_medio?.valor)}
          icone={DollarSign}
          cor="text-purple-600"
          memoria={dados?.cards?.ticket_medio?.memoria}
        />
        <CardComMemoria
          titulo="Custo por Aluno"
          valor={formatCurrency(dados?.cards?.custo_por_aluno?.valor)}
          icone={Users}
          cor="text-gray-600"
        />
      </div>

      {/* Menu de Navegação */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/executivo/dre')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              DRE Gerencial
            </CardTitle>
            <CardDescription>
              Demonstração de Resultado com lucro real operacional
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Ver DRE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/executivo/segmentos')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />
              Análise por Segmento
            </CardTitle>
            <CardDescription>
              Infantil, Fundamental, Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Analisar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/executivo/forecast')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Forecast e Projeções
            </CardTitle>
            <CardDescription>
              Cenários conservador, base e agressivo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Projetar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Composição de Despesas */}
      {dados?.despesas_dre && (
        <Card>
          <CardHeader>
            <CardTitle>Composição de Despesas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(dados.despesas_dre).map(([key, value]) => (
                <div key={key} className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="text-lg font-bold">{formatCurrency(value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
